import {
  Body,
  Button,
  Container,
  Head,
  Heading,
  Html,
  Link,
  Preview,
  Section,
  Text,
} from '@react-email/components'
import { render } from '@react-email/render'

// Mail clients ignore CSS variables and classes, so the STYLE.md values are inlined here
// (packages/ui/src/tokens.ts is the source; keep the two in step).
const color = {
  blue: '#2C57D4', // blue-600, the one primary blue
  text: '#121826', // neutral-900
  muted: '#566175', // neutral-600
  border: '#E1E6ED', // neutral-200
  page: '#F5F7FA', // neutral-50
  white: '#FFFFFF',
}
const font =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

export type MailContent = {
  /** blue heading at the top of the card */
  heading: string
  /** paragraphs above the button; a "\n" inside one is a line break */
  body: string[]
  /** the one main link of the mail, rendered as a blue button */
  action?: { label: string; url: string }
  /** small print below the button */
  footer?: string[]
}

function EnsembleMail({ heading, body, action, footer = [] }: MailContent) {
  const paragraph = { color: color.text, fontSize: '16px', lineHeight: '24px', margin: '0 0 16px' }
  return (
    <Html lang="de">
      <Head />
      <Preview>{heading}</Preview>
      <Body
        style={{ backgroundColor: color.page, fontFamily: font, margin: 0, padding: '32px 16px' }}
      >
        <Container style={{ maxWidth: '560px', margin: '0 auto' }}>
          <Text
            style={{ color: color.blue, fontSize: '20px', fontWeight: 700, margin: '0 0 16px' }}
          >
            ensemble
          </Text>
          <Section
            style={{
              backgroundColor: color.white,
              border: `1px solid ${color.border}`,
              borderRadius: '20px',
              padding: '32px',
            }}
          >
            <Heading
              as="h1"
              style={{
                color: color.blue,
                fontSize: '24px',
                lineHeight: '32px',
                margin: '0 0 24px',
              }}
            >
              {heading}
            </Heading>
            {body.map((text) => (
              <Text
                key={text}
                style={{ ...paragraph, whiteSpace: 'pre-line' }}
              >
                {text}
              </Text>
            ))}
            {action && (
              <>
                <Button
                  href={action.url}
                  style={{
                    backgroundColor: color.blue,
                    borderRadius: '12px',
                    color: color.white,
                    fontSize: '16px',
                    fontWeight: 600,
                    padding: '12px 24px',
                  }}
                >
                  {action.label}
                </Button>
                <Text
                  style={{
                    color: color.muted,
                    fontSize: '13px',
                    lineHeight: '20px',
                    margin: '16px 0 0',
                  }}
                >
                  Falls der Button nicht geht:{' '}
                  <Link
                    href={action.url}
                    style={{ color: color.blue, wordBreak: 'break-all' }}
                  >
                    {action.url}
                  </Link>
                </Text>
              </>
            )}
          </Section>
          {footer.map((text) => (
            <Text
              key={text}
              style={{
                color: color.muted,
                fontSize: '13px',
                lineHeight: '20px',
                margin: '16px 0 0',
                whiteSpace: 'pre-line',
              }}
            >
              {text}
            </Text>
          ))}
        </Container>
      </Body>
    </Html>
  )
}

/** The plain-text part: same content, same order, the link spelled out. */
export function mailText({ heading, body, action, footer = [] }: MailContent) {
  return [heading, ...body, ...(action ? [`${action.label}: ${action.url}`] : []), ...footer].join(
    '\n\n'
  )
}

/** HTML and plain text for one mail - every email ensemble sends goes through this. */
export async function renderMail(content: MailContent) {
  return { html: await render(<EnsembleMail {...content} />), text: mailText(content) }
}
